import {BoxTraverser} from '@mintdata-wsp/common/controllers/surface/BoxVisitor';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {OutlineItemId, SurfacePagesPanelState} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {SurfacePagesOutline} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/panels/outline/pages/SurfacePagesOutline';
import {BoxHostId, BoxId} from '@mintdata/mint-kit/api/models/Identifiers';
import bind from 'bind-decorator';
import {action, IReactionDisposer, reaction} from 'mobx';

export class SurfacePagesPanelController
{
    private _reactionDisposers:IReactionDisposer[] = [];

    public init():void
    {
        this._listenForBoxSelection();
    }

    public dispose():void
    {
        this._reactionDisposers.forEach(disposer => disposer());
    }

    private _listenForBoxSelection():void
    {
        const {surfaceSelection} = controllers.ui;

        const disposer = reaction(
            () => surfaceSelection.getSelectedBoxes().map(({id}) => id),
            () => surfaceSelection.getSelectedBoxes().forEach(this.openOutlineItemParents)
        );

        this._reactionDisposers.push(disposer);
    }

    public getOutlineItemId(boxId:BoxId, hostId:BoxHostId):OutlineItemId
    {
        return hostId ? `${boxId}-${hostId}` : boxId;
    }

    public isOutlineItemOpen(boxId:BoxId, hostId:BoxHostId):boolean
    {
        const {surfaceArea} = controllers.ui;

        const page:Page = surfaceArea.getCurrentLayerPage();
        const box:Box = surfaceArea.findBoxById(page.id, boxId);
        const openOutlineItems:Map<OutlineItemId, boolean> = this._getState().outlineState.openItems;
        const itemId:OutlineItemId = this.getOutlineItemId(boxId, hostId);

        return openOutlineItems.get(itemId) && (box.children.length > 0 || box.componentDesc.isMultiHostContainer);
    }

    @action
    public toggleOutlineItem(boxId:BoxId, hostId:BoxHostId):void
    {
        const openOutlineItems:Map<OutlineItemId, boolean> = this._getState().outlineState.openItems;
        const itemId:OutlineItemId = this.getOutlineItemId(boxId, hostId);

        openOutlineItems.set(itemId, !openOutlineItems.get(itemId));
    }

    @action
    public openOutlineItem(boxId:BoxId, hostId:BoxHostId):void
    {
        const openOutlineItems:Map<OutlineItemId, boolean> = this._getState().outlineState.openItems;
        const itemId:OutlineItemId = this.getOutlineItemId(boxId, hostId);

        openOutlineItems.set(itemId, true);
    }

    @action
    public closeOutlineItem(boxId:BoxId, hostId:BoxHostId):void
    {
        const openOutlineItems:Map<OutlineItemId, boolean> = this._getState().outlineState.openItems;
        const itemId:OutlineItemId = this.getOutlineItemId(boxId, hostId);

        openOutlineItems.set(itemId, false);
    }

    @action.bound
    public openOutlineItemParents(box:Box):void
    {
        BoxTraverser.visit(box, {
            onBox: (parent) => this.openOutlineItem(parent.id, box.hostId)
        }, true);
    }

    @action
    public removeOpenOutlineItem(boxId:BoxId, hostId:BoxHostId):void
    {
        const openOutlineItems:Map<OutlineItemId, boolean> = this._getState().outlineState.openItems;
        const itemId:OutlineItemId = this.getOutlineItemId(boxId, hostId);

        openOutlineItems.delete(itemId);
    }

    @action.bound
    public showRemoveSectionConfirmDialog(sectionName:string):void
    {
        const state:SurfacePagesPanelState = this._getState();
        state.pagesState.showRemoveSectionConfirmDialog = true;
        state.pagesState.removeSectionName = sectionName;
    }

    @action.bound
    public hideRemoveSectionConfirmDialog():void
    {
        const state:SurfacePagesPanelState = this._getState();
        state.pagesState.showRemoveSectionConfirmDialog = false;
        state.pagesState.removeSectionName = null;
    }

    @action.bound
    public setPagesComponentInstance(instance:SurfacePagesOutline | null):void
    {
        const state:SurfacePagesPanelState = this._getState();

        state.pagesState.pagesComponentInstance = instance;
    }

    @bind
    public getPagesComponentInstance():SurfacePagesOutline | null
    {
        const state:SurfacePagesPanelState = this._getState();

        return state.pagesState.pagesComponentInstance;
    }

    @bind
    public shouldShowRemoveSectionConfirmDialog():boolean
    {
        return this._getState().pagesState.showRemoveSectionConfirmDialog;
    }

    @bind
    public getRemoveSectionName():string
    {
        return this._getState().pagesState.removeSectionName;
    }

    private _getState():SurfacePagesPanelState
    {
        return rootState.ui.visualDesigner.surfaceEditor.pagesPanel;
    }
}
